import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../services/api';
import { invalidateLiveCapabilities } from '../../services/liveCapabilitiesStore';
import type { GamificationEvent } from '../../types/auth';
import { subscribeAppForeground } from '../../utils/appForeground';

/**
 * CORE-272/273 — surfaces pending gamification congratulation events (from the
 * CORE-256 feed) so the owner's home can pop a celebration screen, one at a time.
 *
 * Fetches once when `enabled` turns true (best-effort — a failed fetch just
 * yields no popup, never throws into the tree). Events are shown in PRIORITY
 * order: a level-up outranks an achievement when several land in the same batch.
 * `dismiss` acks the current event (so it can't re-pop) and advances to the next,
 * which lets a user who levelled up AND earned an achievement see both in turn.
 *
 * All four Этап-1 event types are handled: level_up → LevelUpModal,
 * achievement_earned → AchievementUnlockedModal, milestone_reached / new_record
 * → MilestoneCelebrationModal. PRIORITY orders which pops first when several land
 * in the same batch.
 *
 * CORE-273 (Tima, 2026-07-30 — «поздравительного экрана нет при достижении
 * нового уровня») — the once-guard used to be STATE (`loaded`) listed in the
 * effect deps. Flipping it inside the effect re-ran the effect on the very next
 * render, whose cleanup set `alive = false` long before the request came back, so
 * the response was always discarded and the queue stayed empty: every celebration
 * screen was dead on every surface, for every event type. The guard is a REF now —
 * it doesn't re-render, so the effect only re-runs when `enabled` actually changes
 * and `alive` only flips on unmount.
 */
const PRIORITY: GamificationEvent['type'][] = ['level_up', 'achievement_earned', 'milestone_reached', 'new_record'];

/**
 * The backend event feed has no push transport. A small foreground-only poll
 * closes the gap between earning an achievement and seeing it without turning
 * every render/navigation into another request. Focus/visibility also refresh
 * immediately, which is the common web flow after completing a practice or
 * using the admin QA tool in another tab.
 */
export const CELEBRATION_REFRESH_MS = 30_000;

/**
 * A streak milestone and its achievement are emitted by the backend in the same
 * transaction. They describe one user-visible moment, so CORE-708 shows the
 * milestone screen once and acknowledges both feed rows when it is dismissed.
 */
const STREAK_ACHIEVEMENT_BY_MILESTONE: Readonly<Record<number, string>> = {
  3: 'first_spark',
  7: 'week_of_light',
  21: 'habit_sprout',
  40: 'forty_days',
  108: 'sacred_mala',
  365: 'wheel_of_year',
};

export type CelebrationQueueItem = {
  /** The single event rendered by the UI. */
  event: GamificationEvent;
  /** Every backend row represented by that screen. */
  ackIds: number[];
};

function createdInSameTransaction(left: GamificationEvent, right: GamificationEvent): boolean {
  const leftTime = Date.parse(left.createdAt);
  const rightTime = Date.parse(right.createdAt);
  return Number.isFinite(leftTime)
    && Number.isFinite(rightTime)
    && leftTime === rightTime;
}

/**
 * Builds the popup queue while keeping acknowledgement boundaries explicit.
 *
 * Duplicate ids are ignored. A milestone is paired only with the exact streak
 * achievement for its threshold and only when both rows carry the same backend
 * timestamp. This intentionally leaves unrelated achievements in the queue.
 */
export function selectCelebrationQueue(
  events: GamificationEvent[] | null | undefined,
): CelebrationQueueItem[] {
  const seenIds = new Set<number>();
  const supported = (events ?? []).filter((event) => {
    if (!PRIORITY.includes(event.type) || seenIds.has(event.id)) return false;
    seenIds.add(event.id);
    return true;
  });

  const pairedAchievementIds = new Set<number>();
  const items = supported.map<CelebrationQueueItem>((event) => {
    if (event.type !== 'milestone_reached' || event.value == null) {
      return { event, ackIds: [event.id] };
    }

    const achievementKey = STREAK_ACHIEVEMENT_BY_MILESTONE[event.value];
    if (!achievementKey) return { event, ackIds: [event.id] };

    // `milestone_reached.referenceKey` has shipped as `streak`, `streak_N`,
    // the semantic achievement key and null across contract revisions. The
    // stable milestone value plus the achievement's exact semantic key and
    // shared transaction timestamp are the safe cross-version join.
    const pair = supported
      .filter((candidate) => (
        candidate.type === 'achievement_earned'
        && candidate.referenceKey === achievementKey
        && !pairedAchievementIds.has(candidate.id)
        && createdInSameTransaction(event, candidate)
      ))
      .sort((left, right) => (
        Math.abs(left.id - event.id) - Math.abs(right.id - event.id)
      ))[0];

    if (!pair) return { event, ackIds: [event.id] };
    pairedAchievementIds.add(pair.id);
    return { event, ackIds: [event.id, pair.id] };
  });

  return items
    .filter((item) => !pairedAchievementIds.has(item.event.id))
    .sort((left, right) => PRIORITY.indexOf(left.event.type) - PRIORITY.indexOf(right.event.type));
}

/**
 * The events worth popping, most important first — level-up outranks an
 * achievement, which outranks a веха / personal best. Exported for the guard test:
 * unknown types are dropped rather than shown as an empty modal.
 */
export function selectCelebrations(events: GamificationEvent[] | null | undefined): GamificationEvent[] {
  return selectCelebrationQueue(events).map((item) => item.event);
}

/**
 * Close is optimistic so a stalled/offline ACK can never trap the user inside
 * a fullscreen modal. The server row remains pending when ACK fails and can be
 * shown again on a later visit; only this screen's explicit ids are ever sent.
 */
export function dismissCelebrationQueueItem(
  item: CelebrationQueueItem,
  acknowledge: (ids: number[]) => Promise<unknown>,
  onDismissed: () => void,
): Promise<unknown> {
  onDismissed();
  return acknowledge(item.ackIds);
}

export function useGamificationCelebration(enabled: boolean) {
  const [queue, setQueue] = useState<CelebrationQueueItem[]>([]);
  const [refreshToken, setRefreshToken] = useState(0);
  const fetching = useRef(false);
  const acknowledgingItem = useRef<CelebrationQueueItem | null>(null);

  useEffect(() => {
    if (!enabled) {
      // Owner-only invariant: a queued popup must disappear immediately when
      // the feature is disabled or the surface switches away from the owner.
      // Resetting the guard also permits a fresh fetch if owner mode returns.
      fetching.current = false;
      acknowledgingItem.current = null;
      setQueue([]);
      return;
    }
    // Do not replace or duplicate a celebration that is already on screen.
    // The next refresh starts after the user has dealt with the current queue.
    if (queue.length > 0 || fetching.current || acknowledgingItem.current) return;
    fetching.current = true;
    let alive = true;
    api.getGamificationEvents()
      .then((res) => {
        if (!alive) return;
        // CORE-817 — a pending level-up means the caller's Live rights may have
        // widened. Drop the cached capabilities so the next read re-fetches the
        // new set; no relogin required. Fires once per level-up (the event is
        // acked after it pops), independent of whether the popup is shown.
        if ((res.events ?? []).some((event) => event.type === 'level_up')) {
          invalidateLiveCapabilities();
        }
        setQueue(selectCelebrationQueue(res.events));
      })
      .catch(() => { /* best-effort: focus/poll will retry */ })
      .finally(() => { fetching.current = false; });
    return () => { alive = false; };
  }, [enabled, queue.length, refreshToken]);

  useEffect(() => {
    if (!enabled || queue.length > 0) return;
    const refresh = () => {
      // Browsers may keep timers alive (heavily throttled) in a hidden tab.
      // Wait for visibility instead of waking the API for an invisible UI.
      if (typeof document !== 'undefined' && document.hidden) return;
      setRefreshToken((token) => token + 1);
    };
    const unsubscribeForeground = subscribeAppForeground(refresh);
    const interval = setInterval(refresh, CELEBRATION_REFRESH_MS);
    return () => {
      clearInterval(interval);
      unsubscribeForeground();
    };
  }, [enabled, queue.length]);

  // Hide synchronously when the owner/feature gate closes; the effect below
  // clears stored state after render and cancels any in-flight fetch response.
  const current = enabled ? queue[0] ?? null : null;
  const event = current?.event ?? null;

  const dismiss = useCallback(() => {
    if (!current || acknowledgingItem.current === current) return;
    acknowledgingItem.current = current;

    void dismissCelebrationQueueItem(
      current,
      (ids) => api.acknowledgeGamificationEvents(ids),
      () => setQueue((items) => (items[0] === current ? items.slice(1) : items)),
    )
      .catch(() => { /* remains pending on BE and may reappear next visit */ })
      .finally(() => {
        if (acknowledgingItem.current === current) acknowledgingItem.current = null;
        // If this was the last queued event, re-read after its ACK settles so an
        // event earned while the screen was open is not stranded until poll.
        setRefreshToken((token) => token + 1);
      });
  }, [current]);

  return { event, dismiss };
}
